import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AdditiveBlending, Color, DoubleSide, ShaderMaterial } from 'three';

interface Props {
  /** World position of the slash center [x, y, z]. */
  position: [number, number, number];
  /** Rotation around Y in radians — orients the crescent's chord direction.
   *  Only used in the default horizontal layout (when `rotation` is absent). */
  rotationY?: number;
  /** Full Euler rotation override. Default is `[-π/2, 0, rotationY]` which
   *  lays the plane FLAT (parallel to ground) for combat impacts. Pass a
   *  custom rotation for vertical / tilted slashes (e.g. chest-emblem swings
   *  use roughly `[0, 0, tilt]` to keep the plane facing forward). */
  rotation?: [number, number, number];
  /** Lifetime in ms. Typical 250 ms for a snap hit-impact read. */
  durationMs?: number;
  /** Plane size in world units (default 1.8). Smaller scales (~0.5) are used
   *  for ambient/relic-idle slashes that should read as flavor, not impact. */
  scale?: number;
  /** Tint override. Defaults to VK pale-cyan / white-hot. Pure-white relic
   *  idle slashes look distinctively "ghostly" against combat hits. */
  colorBase?: string;
  colorHot?: string;
  /** Called when the animation finishes — parent unmounts this. */
  onComplete: () => void;
}

// Vertex shader: passthrough UVs for the fragment's polar-coordinate math.
const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// Fragment shader: paints a horizontal crescent (arc) on a quad. The UV's
// polar angle picks the visible arc-segment; distance-from-center picks the
// blade thickness. Animated by `uProgress`:
//  - 0.0..0.4 : the arc SWEEPS across (left → right) → reads as the blade cutting through
//  - 0.4..1.0 : holds + fades out
// Pure shader = zero geometry overhead. Bloom-friendly additive blending.
const FRAG = /* glsl */ `
uniform float uProgress;   // 0..1 over lifetime
uniform vec3 uColor;
uniform vec3 uColorHot;
varying vec2 vUv;

void main() {
  // Re-center UV to [-1, 1] both axes
  vec2 p = vUv - 0.5;
  // Polar coords. atan returns [-π, π]; remap to 0..1 around the +x axis.
  float angle = atan(p.y, p.x);                   // -π..π
  float angleNorm = (angle + 3.14159) / 6.2832;   // 0..1
  // The crescent occupies the BOTTOM half of the quad (angle from π..2π in
  // our normalized space → angleNorm in 0..0.5). Re-map to [0, 1] for the arc.
  float arcU = angleNorm * 2.0;                   // 0..1 across the arc
  if (arcU < 0.0 || arcU > 1.0) discard;

  // Distance from the unit circle defines blade THICKNESS — we want a
  // visible arc, not a hairline. Widened from 0.02-0.10 to 0.10-0.25.
  float r = length(p) * 2.0;
  float thickness = mix(0.10, 0.25, sin(arcU * 3.14159));
  float bandStart = 0.80 - thickness;
  float bandEnd   = 0.80 + thickness * 0.5;
  float band = smoothstep(bandStart, 0.80, r) * (1.0 - smoothstep(0.80, bandEnd, r));
  if (band < 0.01) discard;

  // Sweep mask: only the left portion of the arc is visible during the
  // early phase, simulating the blade still travelling. After 0.4 the full
  // arc is visible.
  float sweepFront = uProgress < 0.4 ? (uProgress / 0.4) : 1.0;
  float sweepMask = step(arcU, sweepFront);

  // Lifetime fade — slow attack, hard cut on tail.
  float life = uProgress < 0.15
    ? uProgress / 0.15
    : (uProgress < 0.6 ? 1.0 : 1.0 - (uProgress - 0.6) / 0.4);

  // Hot core vs cool halo: brighter near the chord midpoint.
  float chord = sin(arcU * 3.14159);
  vec3 col = mix(uColor, uColorHot, chord);
  // Boosted final alpha — band × sweep × life × 1.5 (clamped at 1.0)
  float alpha = clamp(band * sweepMask * life * 1.5, 0.0, 1.0);
  gl_FragColor = vec4(col, alpha);
}
`;

/**
 * Wind-Slash crescent VFX for Vault-Knight equipped hits. Reads as a single
 * fast blade-stroke leaving a kinetic afterimage, distinguishing VK swings
 * from HK's solid hit-bursts.
 *
 * Implementation: a 0.9u × 0.9u quad with a shader that:
 *  - Discards everything outside a horizontal arc band (polar α + radius)
 *  - Sweeps the arc into view over the first 40% of life
 *  - Fades out over the last 40%
 *  - Additive blends so it glows under bloom without saturating the scene
 */
export function WindSlashArc({
  position,
  rotationY = 0,
  rotation,
  durationMs = 250,
  scale = 1.8,
  colorBase = '#a8e8ff',
  colorHot = '#ffffff',
  onComplete,
}: Props) {
  const matRef = useRef<ShaderMaterial | null>(null);
  const [, setTick] = useState(0);
  const startedAtRef = useRef(Date.now());

  const material = useMemo(() => new ShaderMaterial({
    uniforms: {
      uProgress: { value: 0 },
      uColor:    { value: new Color(colorBase) },
      uColorHot: { value: new Color(colorHot) },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    blending: AdditiveBlending,
    toneMapped: false,
  }), [colorBase, colorHot]);
  matRef.current = material;

  useFrame(() => {
    if (!matRef.current) return;
    const elapsed = (Date.now() - startedAtRef.current) / durationMs;
    matRef.current.uniforms.uProgress.value = Math.min(1, elapsed);
    setTick((n) => (n + 1) & 0xffff);
  });

  useEffect(() => {
    const id = window.setTimeout(onComplete, durationMs);
    return () => window.clearTimeout(id);
  }, [durationMs, onComplete]);

  // The plane lies flat on a slight tilt toward the camera — the crescent's
  // arc-plane should be roughly parallel to the player's swing-arc. Y-rotation
  // is configurable so we can angle the slash per hit.
  // Effective rotation: explicit `rotation` prop wins; otherwise lay the
  // plane flat with the caller's yaw applied around Z (combat-hit layout).
  const effectiveRotation = rotation ?? ([-Math.PI / 2, 0, rotationY] as [number, number, number]);

  return (
    <mesh
      position={position}
      rotation={effectiveRotation}
      material={material}
      // Shader uses discard heavily — the mesh's automatic bounding sphere
      // might be too small for frustum culling. Disable culling to be safe.
      frustumCulled={false}
    >
      <planeGeometry args={[scale, scale]} />
    </mesh>
  );
}
